import { html, render, type TemplateResult } from 'lit-html';
import styles from './pk-resource-row.css?inline';

/**
 * `<pk-resource-row>` — vertical list row (icon/avatar + body + actions).
 *
 * An alternative to `<pk-data-table>` for "list of cards" rendering
 * (Momentos list, Keys list, ActivityLog token / system lists). Three slot
 * regions:
 *   - `lead`    — leading icon / avatar (optional).
 *   - default   — primary text + metadata block.
 *   - `actions` — right-aligned action cluster (optional).
 *
 * When `interactive` is set, the row gets a hover state and a pointer cursor,
 * and a click anywhere outside the actions cluster emits a `select`
 * CustomEvent. Pressing Enter or Space on a focused interactive row also
 * emits `select`, so keyboard users get the same affordance.
 *
 * Attributes:
 *   - `dense`       — boolean; reduces vertical padding.
 *   - `interactive` — boolean; turns the row into a clickable surface.
 */
export class PkResourceRow extends HTMLElement {
    static readonly tagName = 'pk-resource-row';

    static get observedAttributes(): string[] {
        return ['dense', 'interactive'];
    }

    private readonly _root: ShadowRoot;

    constructor() {
        super();
        this._root = this.attachShadow({ mode: 'open' });
    }

    connectedCallback(): void {
        this.render();
    }

    attributeChangedCallback(): void {
        this.render();
    }

    private readonly _onClick = (e: MouseEvent): void => {
        if (!this.hasAttribute('interactive')) return;
        // Don't fire when the click originated inside the actions slot —
        // those buttons should run their own handlers without bubbling into
        // a row-level select.
        const path = e.composedPath();
        const actionsSlot = this._root.querySelector('slot[name="actions"]');
        if (actionsSlot) {
            // Any element actually projected into the actions slot lives
            // outside the shadow root, so checking composedPath() against
            // the slot directly doesn't help. Instead, check for `slot="actions"`
            // on any element in the path.
            for (const target of path) {
                if (target instanceof Element && target.getAttribute('slot') === 'actions') {
                    return;
                }
            }
        }
        this._emitSelect();
    };

    private readonly _onKeydown = (e: KeyboardEvent): void => {
        if (!this.hasAttribute('interactive')) return;
        if (e.key !== 'Enter' && e.key !== ' ') return;
        // Allow Enter/Space inside buttons or inputs to do their own thing.
        const target = e.target as HTMLElement | null;
        if (target && (target.tagName === 'BUTTON' || target.tagName === 'INPUT'
            || target.tagName === 'TEXTAREA' || target.tagName === 'A')) return;
        e.preventDefault();
        this._emitSelect();
    };

    private _emitSelect(): void {
        this.dispatchEvent(new CustomEvent('select', { bubbles: true, composed: true }));
    }

    private template(): TemplateResult {
        const dense       = this.hasAttribute('dense');
        const interactive = this.hasAttribute('interactive');
        return html`
            <style>${styles}</style>
            <div class="row
                    ${dense ? 'dense' : ''}
                    ${interactive ? 'interactive' : ''}"
                role=${interactive ? 'button' : 'group'}
                tabindex=${interactive ? '0' : '-1'}
                @click=${this._onClick}
                @keydown=${this._onKeydown}
            >
                <div class="lead">
                    <slot name="lead"></slot>
                </div>
                <div class="body">
                    <slot></slot>
                </div>
                <div class="actions">
                    <slot name="actions"></slot>
                </div>
            </div>
        `;
    }

    private render(): void {
        render(this.template(), this._root);
    }
}

if (!customElements.get(PkResourceRow.tagName)) {
    customElements.define(PkResourceRow.tagName, PkResourceRow);
}

declare global {
    interface HTMLElementTagNameMap {
        'pk-resource-row': PkResourceRow;
    }
}
